// Commands in words: what each one asks for, said shortly. Used where a
// command is named to the person outside the key guide: beside a pending
// key, and when a command has nothing to act on.

import type { Command, Menu, Obj, Place, Presentation, Verb } from './command';

export const NOUN: Readonly<Record<Obj, string>> = {
  line: 'line',
  change: 'change',
  scope: 'scope',
  file: 'file',
  draft: 'draft',
  thread: 'thread',
  pr: 'pull request',
  group: 'group',
};

const VERB: Readonly<Record<Verb, string>> = {
  review: 'Mark reviewed',
  unmark: 'Remove the review mark',
  comment: 'Comment',
  edit: 'Suggest an edit',
  copy: 'Copy a link',
  discard: 'Discard',
  include: 'Include in the review, or take out',
  open: 'Open',
};

const PLACE: Readonly<Record<Place, string>> = {
  overview: 'Go to Overview',
  files: 'Go to Files',
  commits: 'Go to Snapshots',
  review: 'Go to Review',
  top: 'Go to the top',
  end: 'Go to the end',
  'named-file': 'Find the file the selected text names',
  repository: 'Go to the repository',
  branches: 'Go to its branches',
  repositories: 'Go to the repositories',
  history: 'Go to the history',
};

const PRESENT: Readonly<Record<Presentation, string>> = {
  centre: 'Centre the cursor',
  preview: 'Switch between source and preview',
  'file-list': 'Hide or show the file list',
  wrap: 'Turn line wrapping on or off',
  layout: 'Switch between split and unified',
  algorithm: 'Use the next diff algorithm',
  minimap: 'Switch the minimap between rich and thin',
};

const MENU: Readonly<Record<Menu, string>> = {
  finder: 'Find',
  stack: 'Open the stack menu',
  guide: 'Show the key guide',
  feedback: 'Send feedback',
  settings: 'Open Settings',
  jump: 'Jump to…',
  base: 'Pick the base',
  head: 'Pick the head',
};

/** What `command` asks for, in a few words. */
export function phrase(command: Command): string {
  switch (command.kind) {
    case 'move':
      return `${command.dir > 0 ? 'Next' : 'Previous'} ${command.only ? command.only + ' ' : ''}${NOUN[command.object]}`;
    case 'scroll': {
      const way = command.axis === 'y' ? (command.dir > 0 ? 'down' : 'up') : command.dir > 0 ? 'right' : 'left';
      return command.by === 'half-page' ? `Scroll half a page ${way}` : `Scroll ${way}`;
    }
    case 'select':
      return `Select the ${NOUN[command.object]}`;
    case 'range':
      return 'Start or clear a line range';
    case 'grow':
      return command.dir > 0 ? 'Grow the selection' : 'Shrink the selection';
    case 'act':
      // Taking a draft out never moves on, so the words say which half does.
      if (command.advance && command.verb === 'include') return 'Include in the review and move on, or take out';
      return command.advance ? `${VERB[command.verb]} and move on` : VERB[command.verb];
    case 'goto':
      return PLACE[command.place];
    case 'fold': {
      const what = command.level ? ' every scope at this depth' : command.object ? ` the ${NOUN[command.object]}` : '';
      return `${command.to === 'toggle' ? 'Fold or unfold' : command.to === 'open' ? 'Unfold' : 'Fold'}${what}`;
    }
    case 'present':
      return PRESENT[command.what];
    case 'open':
      return MENU[command.menu];
    case 'refresh':
      return 'Refresh';
    case 'submit':
      return 'Send';
    case 'cancel':
      return 'Cancel';
  }
}
